import React from 'react'
import { AppBasicTable } from "@components"
import { avgGapColumn, dataColumn, websiteColumn } from "Pages/AnalyzersV2/@components/Table/columns"
import AnalyzerTableToolbar from "Pages/AnalyzersV2/@components/Table/Toolbar"
import { postExcel, stringToDate } from '@ui-seeker/utils'
import { BACKEND_APIS } from "@constants"
import { analyzerFilters } from "Pages/AnalyzersV2/@signals"
import { useToast } from "@hooks"
import AnalyzerHeaderTable from "Pages/AnalyzersV2/@components/Table/columns/Data/Header"
import { isMainRow } from "Pages/AnalyzersV2/@components/Table/@utils"


export default function AnalyzersTable(
	{
		tableData,
		columns,
		evolutionData,
		isRating = false,
		isBrokers = false,
		hideIcon = false,
		isPosition = false,
		mainName
	}) {
	const { showToast } = useToast()
	let rowIndex = 0
	const isWeekend = date => {
		// The pinned "Avg gap" column has no date behind it.
		if (!date) return false

		const day = stringToDate(date).getDay()
		return day === 0 || day === 6
	}

	// Position pins "Avg gap" right after the website column, so the date columns
	// start one slot further right.
	const WEBSITE_COLUMN_WIDTH = 150
	const AVG_GAP_COLUMN_WIDTH = 90
	const firstDateIndex = isPosition ? 2 : 1
	const isAvgGapColumn = rowIdx => isPosition && rowIdx === 1
	const stickyLeft = rowIdx => rowIdx === 0
		? 0
		: isAvgGapColumn(rowIdx) ? WEBSITE_COLUMN_WIDTH : undefined
	const isSticky = rowIdx => stickyLeft(rowIdx) !== undefined

	return <AppBasicTable
		Components={{
			Title: () => <AnalyzerTableToolbar isRating={isRating} isPosition={isPosition} />
		}}
		onDownload={() => postExcel(`${BACKEND_APIS.DASHBOARD}/excel`, analyzerFilters.value)}
		setHeaderCellProps={(_, rowIdx) => ({
			sx: {
				...isSticky(rowIdx) && {
					position: 'sticky',
					left: stickyLeft(rowIdx),
					zIndex: 3,
					...isAvgGapColumn(rowIdx) && { minWidth: AVG_GAP_COLUMN_WIDTH },
				},
				p: .5,
				textAlign: rowIdx === 0 && "left !important",
				backgroundColor: isWeekend(columns[rowIdx - firstDateIndex]) ? 'error.main' : 'primary.main',
				pl: rowIdx === 0 && 1,
			}
		})}
		setBodyCellProps={(_, row, idx, rowIdx) => {
			if (rowIdx === 0) {
				rowIndex++
			}
			const highlightMain = isPosition && isMainRow(row)
			const sticky = isSticky(rowIdx)
			return {
				sx: {
					position: sticky && 'sticky',
					left: sticky ? stickyLeft(rowIdx) : 0,
					p: 0,
					backgroundColor: sticky && (highlightMain
						? "primary.lighter"
						: (rowIndex % 2 === 0 ? "background.lightGrey" : "background.grey")),
					// Only position pins a second column, and only there do the pinned
					// cells need to outrank the scrolling ones.
					...isPosition && sticky && {
						zIndex: 2,
						...isAvgGapColumn(rowIdx) && { minWidth: AVG_GAP_COLUMN_WIDTH },
					},
				}
			}
		}}
		{...isPosition && {
			setBodyRowsProps: (row, idx) => ({
				sx: {
					backgroundColor: isMainRow(row)
						? "primary.lighter"
						: (idx % 2 === 0 ? "background.grey" : "background.lightGrey"),
				}
			})
		}}
		headers={[[
			{ label: isBrokers ? "Provider" : "Website" },
			...isPosition ? [{ label: "Avg gap" }] : [],
			...columns.map(label => ({
				render: () => <AnalyzerHeaderTable label={label} {...evolutionData}/>
			}))
		]]}
		options={{
			onDownload: () => showToast.promise(postExcel(`${BACKEND_APIS.DASHBOARD}/excel`, analyzerFilters.value))
		}}
		data={tableData}
		columns={[
			websiteColumn(isBrokers, hideIcon, isPosition),
			...isPosition ? [avgGapColumn(mainName)] : [],
			// Position drops the per-cell evolution button: the column header still
			// opens the evolution for the whole date.
			...columns?.map(c => dataColumn(c, evolutionData, { isRating, isPosition }))
		]}
	/>
}